import React, { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon as CheckCircle, TagIcon as Tag, XIcon as X } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";

/**
 * Inline promo code input + apply button.
 * Props:
 *   kind   — package kind ("plan", "one_time_dog", "certificate", etc.)
 *   amount — base amount before discount
 *   onApplied(promo) — called with {code, discount, final, ...} when valid
 *   onCleared()      — called when user removes the applied code
 *
 * Keeps its own local state. Parent passes the resolved `code` back into
 * the /billing/checkout payload as `promo_code` to actually apply at payment.
 */
export default function PromoCodeInput({ kind, amount, onApplied, onCleared, applied }) {
  const [code, setCode] = useState("");
  const [validating, setValidating] = useState(false);
  const [err, setErr] = useState("");
  // App Review 3.1.1: promo/voucher codes may not unlock or discount
  // digital content inside the native app. Codes still apply at the
  // web checkout itself.
  if (isNativeApp()) return null;

  const apply = async () => {
    setErr("");
    setValidating(true);
    try {
      const { data } = await api.post("/promo-codes/validate", {
        code,
        kind,
        base_amount: amount,
      });
      if (!data.valid) {
        setErr(data.error || "Invalid code");
        return;
      }
      onApplied(data);
      setCode("");
    } catch (e) {
      setErr("Could not validate code");
    } finally {
      setValidating(false);
    }
  };

  if (applied?.valid) {
    return (
      <div
        className="flex items-center justify-between gap-2 bg-[#C1272D]/5 border border-[#C1272D]/30 rounded-lg px-3 py-2 text-sm"
        data-testid="promo-applied"
      >
        <div className="flex items-center gap-2 min-w-0">
          <CheckCircle size={16} weight="fill" className="text-crimson flex-shrink-0" />
          <span className="font-mono font-bold text-ink truncate">{applied.code}</span>
          <span className="text-[#6B6B6B] text-xs">−${applied.discount.toFixed(2)}</span>
        </div>
        <button
          onClick={onCleared}
          className="text-[#6B6B6B] hover:text-crimson"
          aria-label="Remove promo"
          data-testid="promo-clear"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="promo-input-block">
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Promo code"
          className="rounded-lg uppercase font-mono"
          data-testid="promo-code-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={apply}
          disabled={!code || validating}
          className="rounded-lg border-[#E5E5E5]"
          data-testid="promo-apply-btn"
        >
          <Tag size={14} className="mr-1" /> Apply
        </Button>
      </div>
      {err && <p className="text-xs text-crimson mt-1.5">{err}</p>}
    </div>
  );
}
